import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { useOrgTimezone } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { ClinicalTimeField, toOccurredAt } from '@/features/flowsheets/ClinicalEventEntry';
import type { ClinicalEpisode } from '@/features/flowsheets/types';
import type { OfflineEpisodeContext } from '@/features/flowsheets/offlineContext';
export interface UltrasoundProposal {
  id: string;
  status: string;
  metric: string;
  value: string;
  unit: string;
  evidence_span: string;
}
export interface UltrasoundImportSource {
  version: number;
  revision_id: string;
  source_hash: string;
  patient_id: string | null;
  clinical_proposals?: UltrasoundProposal[];
}
export function UltrasoundEvidenceImport({
  organizationId,
  documentId,
  source,
  onImported,
}: {
  organizationId: string;
  documentId: string;
  source: UltrasoundImportSource;
  onImported: () => void;
}) {
  const { can } = usePermissions();
  const allowed =
    ['read_document', 'read_patient', 'read_flowsheet'].every((p) => can(p)) &&
    (can('create_clinical_event') || can('create_supervised_clinical_event'));
  if (!allowed)
    return (
      <p>
        Reviewed flowsheet import requires clinical event creation permission, plus document, patient and flowsheet
        access.
      </p>
    );
  if (!source.patient_id) return <p>Confirm the source patient before choosing an import destination.</p>;
  return (
    <AuthorizedImport
      key={`${organizationId}:${documentId}:${source.patient_id}`}
      organizationId={organizationId}
      documentId={documentId}
      source={source}
      onImported={onImported}
    />
  );
}
function AuthorizedImport({
  organizationId,
  documentId,
  source,
  onImported,
}: {
  organizationId: string;
  documentId: string;
  source: UltrasoundImportSource;
  onImported: () => void;
}) {
  const { timeZone } = useOrgTimezone(),
    cache = useQueryClient();
  const [pregnancy, setPregnancy] = useState(''),
    [episode, setEpisode] = useState(''),
    [subject, setSubject] = useState(''),
    [encounter, setEncounter] = useState(''),
    [time, setTime] = useState(''),
    [selected, setSelected] = useState<string[]>([]),
    [reviewed, setReviewed] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState('');
  const [reviewedSource, setReviewedSource] = useState('');
  const sourceKey = `${source.revision_id}:${source.source_hash}:${source.version}`;
  const frozen = useRef<{ body: Record<string, unknown>; key: string } | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const base = `/api/v1/organizations/${organizationId}`;
  const pregnancies = useQuery({
    queryKey: ['us-import-pregnancies', organizationId, source.patient_id],
    queryFn: async () =>
      (
        await clientApi.get<{ data: { id: string; edd: string | null; status: string }[] }>(
          `${base}/patients/${source.patient_id}/pregnancies`,
        )
      ).data.data,
  });
  const episodes = useQuery({
    queryKey: ['us-import-episodes', organizationId, pregnancy],
    enabled: !!pregnancy,
    queryFn: async () =>
      (await clientApi.get<ClinicalEpisode[]>(`${base}/flowsheets/episodes`, { params: { pregnancy_id: pregnancy } }))
        .data,
  });
  const prepared = useQuery({
    queryKey: ['us-import-preparation', organizationId, episode],
    enabled: !!episode,
    queryFn: async () =>
      (
        await clientApi.get<{ episode: ClinicalEpisode; context: OfflineEpisodeContext }>(
          `${base}/flowsheets/episodes/${episode}/preparation`,
        )
      ).data,
  });
  const proposals = (source.clinical_proposals ?? []).filter((p) => p.status === 'REVIEWED');
  const subjects =
    prepared.data?.episode.subjects.filter((s) => s.subject_type === 'FETUS' && s.active !== false) ?? [];
  const encounters =
    prepared.data?.context.encounters.filter(
      (e) => e.pregnancy_id === pregnancy && (!e.patient_id || e.patient_id === source.patient_id),
    ) ?? [];
  function invalidateReview() {
    setReviewed(false);
  }
  async function submit() {
    setMessage('');
    try {
      if (!frozen.current) {
        if (
          !reviewed ||
          reviewedSource !== sourceKey ||
          !selected.length ||
          !pregnancy ||
          !episode ||
          !subject ||
          !encounter
        )
          throw Error('Choose the exact destination and reviewed evidence, and clinical time.');
        if (
          !subjects.some((s) => s.id === subject) ||
          !encounters.some((e) => e.id === encounter) ||
          selected.some((id) => !proposals.some((p) => p.id === id))
        )
          throw Error('The destination or accepted source evidence changed. Refresh and review again.');
        const selectedMetrics = selected.map((id) => proposals.find((p) => p.id === id)!.metric);
        if (new Set(selectedMetrics).size !== selectedMetrics.length)
          throw Error('Select only one accepted source value per measurement for this fetus and time.');
        frozen.current = {
          key: sourceKey,
          body: {
            document_id: documentId,
            review_version: source.version,
            revision_id: source.revision_id,
            source_hash: source.source_hash,
            proposal_ids: selected,
            fetus_subject_id: subject,
            encounter_id: encounter,
            observed_at: toOccurredAt(time, timeZone),
            request_id: crypto.randomUUID(),
          },
        };
      }
      setBusy(true);
      setUncertain(true);
      await clientApi.post(`${base}/flowsheets/episodes/${episode}/import-ultrasound`, frozen.current.body);
      setUncertain(false);
      frozen.current = null;
      setSelected([]);
      setReviewed(false);
      setMessage(
        'Evidence imported as pending clinical review. Open the selected flowsheet and review the source-linked entries before using them in confirmed results.',
      );
      await cache.invalidateQueries({ queryKey: ['clinical-events', organizationId, episode] });
      onImported();
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (status && status >= 400 && status < 500) {
        setUncertain(false);
        frozen.current = null;
        setReviewed(false);
      }
      setMessage(
        status === 409
          ? 'The source or destination changed. Your selections are retained; refresh the document and explicitly review again.'
          : status === 403
            ? 'Import permission was denied. No automatic retry; your selections are retained.'
            : error instanceof Error
              ? error.message
              : 'Import failed. Your selections are retained.',
      );
    } finally {
      setBusy(false);
    }
  }
  const blocked = busy || uncertain;
  return (
    <section className="flex flex-col gap-3 rounded border p-4" aria-label="Reviewed ultrasound import">
      <h4 className="font-semibold">Import accepted ultrasound evidence</h4>
      <p className="text-sm">
        Select the exact pregnancy, fetus and encounter. Import creates source-linked observations pending clinical
        review; it does not infer a fetus or replace existing measurements.
      </p>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="us-pregnancy">Pregnancy</FieldLabel>
          <select
            id="us-pregnancy"
            className="min-h-11 rounded border p-2"
            disabled={blocked}
            value={pregnancy}
            onChange={(e) => {
              setPregnancy(e.target.value);
              setEpisode('');
              setSubject('');
              setEncounter('');
              invalidateReview();
            }}>
            <option value="">Choose pregnancy</option>
            {pregnancies.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.edd ? `EDD ${p.edd}` : 'EDD not recorded'} · {p.status}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          <FieldLabel htmlFor="us-episode">Clinical episode</FieldLabel>
          <select
            id="us-episode"
            className="min-h-11 rounded border p-2"
            disabled={blocked || !pregnancy}
            value={episode}
            onChange={(e) => {
              setEpisode(e.target.value);
              setSubject('');
              setEncounter('');
              invalidateReview();
            }}>
            <option value="">Choose episode</option>
            {episodes.data
              ?.filter((e) => !e.administration?.closed)
              .map((e, i) => (
                <option key={e.id} value={e.id}>
                  Episode {i + 1} · {e.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </Field>
        <Field>
          <FieldLabel htmlFor="us-fetus">Fetus</FieldLabel>
          <select
            id="us-fetus"
            className="min-h-11 rounded border p-2"
            disabled={blocked || !episode}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              invalidateReview();
            }}>
            <option value="">Choose fetus</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field>
          <FieldLabel htmlFor="us-encounter">Encounter</FieldLabel>
          <select
            id="us-encounter"
            className="min-h-11 rounded border p-2"
            disabled={blocked || !episode}
            value={encounter}
            onChange={(e) => {
              setEncounter(e.target.value);
              invalidateReview();
            }}>
            <option value="">Choose encounter</option>
            {encounters.map((e) => (
              <option key={e.id} value={e.id}>
                {e.encounter_date} · {e.encounter_type ?? 'Encounter'}
              </option>
            ))}
          </select>
        </Field>
        <fieldset disabled={blocked}>
          <ClinicalTimeField
            value={time}
            onChange={(v) => {
              setTime(v);
              invalidateReview();
            }}
            timeZone={timeZone}
          />
        </fieldset>
        {proposals.map((p) => (
          <Field key={p.id} orientation="horizontal">
            <Checkbox
              id={`us-${p.id}`}
              disabled={blocked}
              checked={selected.includes(p.id)}
              onCheckedChange={(v) => {
                setSelected(v === true ? [...selected, p.id] : selected.filter((id) => id !== p.id));
                invalidateReview();
              }}
            />
            <FieldLabel htmlFor={`us-${p.id}`}>
              {p.metric}: {p.value} {p.unit}
              <span className="block whitespace-pre-wrap text-sm font-normal">{p.evidence_span}</span>
            </FieldLabel>
          </Field>
        ))}
        {!proposals.length && <p>No accepted source evidence is available. Review the original source values first.</p>}

        <Field orientation="horizontal">
          <Checkbox
            id="us-import-reviewed"
            disabled={blocked}
            checked={reviewed && reviewedSource === sourceKey}
            onCheckedChange={(v) => {
              setReviewed(v === true);
              setReviewedSource(sourceKey);
            }}
          />
          <FieldLabel htmlFor="us-import-reviewed">
            I reviewed the original report, patient, pregnancy, fetus, encounter, measurement units and observation
            time.
          </FieldLabel>
        </Field>
      </FieldGroup>
      {(pregnancies.isError || episodes.isError || prepared.isError) && (
        <p role="alert">Import destinations could not be loaded. Reconnect and reopen the document to retry.</p>
      )}
      {reviewedSource && reviewedSource !== sourceKey && (
        <p role="alert">The document review changed. Confirm the current evidence again before import.</p>
      )}
      {uncertain && (
        <p role="alert">
          Import result is uncertain. Retry the same import before changing selections; the same request identity is
          retained.
        </p>
      )}
      <Button
        className="min-h-11"
        disabled={busy || (!uncertain && (!reviewed || reviewedSource !== sourceKey || !selected.length))}
        onClick={submit}>
        {uncertain ? 'Retry same reviewed import' : 'Import reviewed evidence'}
      </Button>
      <Button
        type="button"
        variant="outline"
        className="min-h-11"
        disabled={blocked}
        onClick={() => {
          invalidateReview();
          void cache.invalidateQueries({ queryKey: ['document-review', organizationId, 'uploaded', documentId] });
          void pregnancies.refetch();
          if (pregnancy) void episodes.refetch();
          if (episode) void prepared.refetch();
        }}>
        Refresh source and destinations
      </Button>
      {message && <p role="status">{message}</p>}
    </section>
  );
}
